import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { TIMESHEET_SLOT_TABS } from "../constants";

dayjs.extend(customParseFormat);

const CLOCK_FORMATS = ["HH:mm", "H:mm", "hh:mm A", "h:mm A", "HH:mm:ss"];

const readAttr = (node, key) => node?.[key] ?? node?.attributes?.[key];

const readEventRoleRoleId = (role = {}) =>
  readAttr(role, "role_id") ??
  readAttr(role, "role")?.id ??
  readAttr(role, "role")?.attributes?.id;

const parseMarginMinutes = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readShifts = (role = {}) =>
  readAttr(role, "event_role_shifts") ??
  readAttr(role, "event_role_shifts_attributes") ??
  [];

const readTraining = (shift = {}) =>
  readAttr(shift, "training") ?? readAttr(shift, "training_attributes");

const isPaidTraining = (training) =>
  Boolean(
    training &&
      (readAttr(training, "starts_at") ||
        readAttr(training, "is_paid_training") ||
        readAttr(training, "payroll_amount")),
  );

export const getApplicantRoleId = (applicant = {}) =>
  applicant?.role?.id ?? applicant?.role_id ?? null;

export const getShiftsForApplicant = (event, applicant) => {
  const applicantRoleId = getApplicantRoleId(applicant);
  const roles = event?.event_roles ?? event?.event_roles_attributes ?? [];

  return roles.flatMap((role) => {
    if (
      applicantRoleId &&
      String(readEventRoleRoleId(role)) !== String(applicantRoleId)
    ) {
      return [];
    }

    return readShifts(role).map((shift) => ({ role, shift }));
  });
};

export const getTrainingsForApplicant = (event, applicant) =>
  getShiftsForApplicant(event, applicant)
    .map(({ shift }) => readTraining(shift))
    .filter(isPaidTraining);

const getEventPaidTrainings = (event) => {
  const roles = event?.event_roles ?? event?.event_roles_attributes ?? [];

  return roles.flatMap((role) =>
    readShifts(role)
      .map((shift) => readTraining(shift))
      .filter(isPaidTraining),
  );
};

/** True when any paid training has started (today or earlier) — even if event is still upcoming. */
export const hasEventTrainingStarted = (event) => {
  const today = dayjs().startOf("day");

  return getEventPaidTrainings(event).some((training) => {
    const startsAt = readAttr(training, "starts_at");
    if (!startsAt) {
      return false;
    }

    const startDay = dayjs(startsAt).startOf("day");
    return startDay.isValid() && !today.isBefore(startDay, "day");
  });
};

/** Same shape as getEventDateBounds, but uses training starts_at / ends_at. */
export const getEventTrainingDateBounds = (event) => {
  const trainings = getEventPaidTrainings(event);
  const today = dayjs().endOf("day");

  if (!trainings.length) {
    return {
      minDate: undefined,
      maxDate: today,
    };
  }

  let trainingStart;
  let trainingEnd;

  trainings.forEach((training) => {
    const startsAt = readAttr(training, "starts_at");
    const endsAt = readAttr(training, "ends_at");
    const start = startsAt ? dayjs(startsAt).startOf("day") : null;
    const end = endsAt ? dayjs(endsAt).endOf("day") : null;

    if (start?.isValid() && (!trainingStart || start.isBefore(trainingStart))) {
      trainingStart = start;
    }

    if (end?.isValid() && (!trainingEnd || end.isAfter(trainingEnd))) {
      trainingEnd = end;
    }
  });

  let maxDate = today;
  if (trainingEnd?.isValid() && trainingEnd.isBefore(maxDate)) {
    maxDate = trainingEnd;
  }

  return {
    minDate: trainingStart?.isValid() ? trainingStart : undefined,
    maxDate: maxDate.isValid() ? maxDate : today,
  };
};

const resolveClockOnDate = (dateStr, clockValue) => {
  if (!dateStr?.trim() || clockValue == null || clockValue === "") {
    return null;
  }

  const onDate = dayjs(dateStr).startOf("day");
  if (!onDate.isValid()) {
    return null;
  }

  const raw = String(clockValue);
  if (raw.includes("T")) {
    const parsed = dayjs(clockValue);
    if (!parsed.isValid()) {
      return null;
    }

    return onDate
      .hour(parsed.hour())
      .minute(parsed.minute())
      .second(parsed.second())
      .millisecond(0);
  }

  let parsed = dayjs(clockValue, CLOCK_FORMATS, true);
  if (!parsed.isValid()) {
    parsed = dayjs(clockValue, CLOCK_FORMATS);
  }
  if (!parsed.isValid()) {
    return null;
  }

  return onDate
    .hour(parsed.hour())
    .minute(parsed.minute())
    .second(0)
    .millisecond(0);
};

const isOvernightShift = (startsAt, endsAt) => {
  const start = resolveClockOnDate("2000-01-01", startsAt);
  const end = resolveClockOnDate("2000-01-01", endsAt);
  if (!start?.isValid() || !end?.isValid()) {
    return false;
  }

  return !end.isAfter(start);
};

const getShiftStartAnchorDays = (dateStr, startsAt, endsAt) => {
  const day = dayjs(dateStr).startOf("day");
  if (!day.isValid()) {
    return [];
  }

  if (!isOvernightShift(startsAt, endsAt)) {
    return [day];
  }

  return [day.subtract(1, "day"), day, day.add(1, "day")];
};

const resolveShiftEndOnAnchor = (anchorDay, startsAt, endsAt) => {
  let shiftEnd = resolveClockOnDate(anchorDay.format("YYYY-MM-DD"), endsAt);
  if (!shiftEnd?.isValid()) {
    return null;
  }

  if (!isOvernightShift(startsAt, endsAt)) {
    return shiftEnd;
  }

  const shiftStart = resolveClockOnDate(
    anchorDay.format("YYYY-MM-DD"),
    startsAt,
  );
  if (shiftStart?.isValid() && !shiftEnd.isAfter(shiftStart)) {
    shiftEnd = shiftEnd.add(1, "day");
  }

  return shiftEnd;
};

const getShiftEndAnchorDays = (dateStr, startsAt, endsAt) => {
  const day = dayjs(dateStr).startOf("day");
  if (!day.isValid()) {
    return [];
  }

  if (!isOvernightShift(startsAt, endsAt)) {
    return [day];
  }

  return [day.subtract(1, "day"), day, day.add(1, "day")];
};

const isWithinWindow = (dateTime, from, to) =>
  Boolean(
    dateTime?.isValid() &&
      from?.isValid() &&
      to?.isValid() &&
      !dateTime.isBefore(from) &&
      !dateTime.isAfter(to),
  );

const isShiftCheckInWithinMargin = (dateTime, shift, dateStr) => {
  const startsAt = readAttr(shift, "starts_at");
  const endsAt = readAttr(shift, "ends_at");
  const before = parseMarginMinutes(readAttr(shift, "before_check_in_margin"));
  const after = parseMarginMinutes(readAttr(shift, "after_check_in_margin"));

  return getShiftStartAnchorDays(dateStr, startsAt, endsAt).some((anchorDay) => {
    const shiftStart = resolveClockOnDate(
      anchorDay.format("YYYY-MM-DD"),
      startsAt,
    );
    if (!shiftStart?.isValid()) {
      return false;
    }

    const from = shiftStart.subtract(before, "minute");
    const to = shiftStart.add(after, "minute");

    return isWithinWindow(dateTime, from, to);
  });
};

const isShiftCheckOutWithinMargin = (dateTime, shift, dateStr) => {
  const startsAt = readAttr(shift, "starts_at");
  const endsAt = readAttr(shift, "ends_at");
  const before = parseMarginMinutes(readAttr(shift, "before_check_out_margin"));
  const after = parseMarginMinutes(readAttr(shift, "after_check_out_margin"));

  return getShiftEndAnchorDays(dateStr, startsAt, endsAt).some((anchorDay) => {
    const shiftEnd = resolveShiftEndOnAnchor(anchorDay, startsAt, endsAt);
    if (!shiftEnd?.isValid()) {
      return false;
    }

    const from = shiftEnd.subtract(before, "minute");
    const to = shiftEnd.add(after, "minute");

    return isWithinWindow(dateTime, from, to);
  });
};

const isDateWithinTrainingDays = (dateStr, training) => {
  const day = dayjs(dateStr).startOf("day");
  const trainingStart = dayjs(readAttr(training, "starts_at")).startOf("day");
  const trainingEnd = dayjs(readAttr(training, "ends_at")).startOf("day");

  if (!day.isValid() || !trainingStart.isValid()) {
    return false;
  }

  if (day.isBefore(trainingStart, "day")) {
    return false;
  }

  if (trainingEnd.isValid() && day.isAfter(trainingEnd, "day")) {
    return false;
  }

  return true;
};

/** Multi-day trainings reuse start/end clock times on each day in the range. */
const isTrainingCheckInWithinMargin = (dateTime, training, dateStr) => {
  if (!isDateWithinTrainingDays(dateStr, training)) {
    return false;
  }

  const trainingStart = dayjs(readAttr(training, "starts_at"));
  if (!trainingStart.isValid()) {
    return false;
  }

  const dayStart = resolveClockOnDate(
    dateStr,
    trainingStart.format("HH:mm:ss"),
  );
  if (!dayStart?.isValid()) {
    return false;
  }

  const from = dayStart.subtract(
    parseMarginMinutes(readAttr(training, "before_check_in_margin")),
    "minute",
  );
  const to = dayStart.add(
    parseMarginMinutes(readAttr(training, "after_check_in_margin")),
    "minute",
  );

  return isWithinWindow(dateTime, from, to);
};

const isTrainingCheckOutWithinMargin = (dateTime, training, dateStr) => {
  if (!isDateWithinTrainingDays(dateStr, training)) {
    return false;
  }

  const trainingEnd = dayjs(readAttr(training, "ends_at"));
  if (!trainingEnd.isValid()) {
    return false;
  }

  const dayEnd = resolveClockOnDate(dateStr, trainingEnd.format("HH:mm:ss"));
  if (!dayEnd?.isValid()) {
    return false;
  }

  const from = dayEnd.subtract(
    parseMarginMinutes(readAttr(training, "before_check_out_margin")),
    "minute",
  );
  const to = dayEnd.add(
    parseMarginMinutes(readAttr(training, "after_check_out_margin")),
    "minute",
  );

  return isWithinWindow(dateTime, from, to);
};

export const isCheckInWithinSlotMargin = ({
  dateStr,
  timeStr,
  slotType,
  event,
  applicant,
}) => {
  const dateTime = dayjs(`${dateStr}T${timeStr}`);
  if (!dateTime.isValid()) {
    return true;
  }

  if (slotType === TIMESHEET_SLOT_TABS.trainings) {
    const trainings = getTrainingsForApplicant(event, applicant);
    if (!trainings.length) {
      return true;
    }

    return trainings.some((training) =>
      isTrainingCheckInWithinMargin(dateTime, training, dateStr),
    );
  }

  const shifts = getShiftsForApplicant(event, applicant);
  if (!shifts.length) {
    return true;
  }

  return shifts.some(({ shift }) =>
    isShiftCheckInWithinMargin(dateTime, shift, dateStr),
  );
};

export const isCheckOutWithinSlotMargin = ({
  dateStr,
  timeStr,
  slotType,
  event,
  applicant,
}) => {
  const dateTime = dayjs(`${dateStr}T${timeStr}`);
  if (!dateTime.isValid()) {
    return true;
  }

  if (slotType === TIMESHEET_SLOT_TABS.trainings) {
    const trainings = getTrainingsForApplicant(event, applicant);
    if (!trainings.length) {
      return true;
    }

    return trainings.some((training) =>
      isTrainingCheckOutWithinMargin(dateTime, training, dateStr),
    );
  }

  const shifts = getShiftsForApplicant(event, applicant);
  if (!shifts.length) {
    return true;
  }

  return shifts.some(({ shift }) =>
    isShiftCheckOutWithinMargin(dateTime, shift, dateStr),
  );
};
